import type { CompareProduct } from '@iheartjane/menu';
import { MAX_COMPARE, useCompare } from '@iheartjane/menu';
import { AddIcon, Button, CheckIcon, useToast } from '@iheartjane/reefer';

import { useCompareBarVisibilityActions } from '@jane/bloom/components/compareProducts/compareBarVisibility';
import { trackAddedProductToCompare } from '@jane/bloom/services/tracking/track';

import styles from './compareProductButton.module.css';

interface CompareProductButtonProps {
  className?: string;
  compareProduct: CompareProduct;
  testId: string;
}

/**
 * The PDP's compare control, shared by both placements: the mobile pill above
 * add-to-bag and the button over the product image on wider screens. Holds all
 * the behaviour; the wrappers only position it.
 */
export const CompareProductButton = ({
  className,
  compareProduct,
  testId,
}: CompareProductButtonProps) => {
  const { isSelected, openModal, productCount, setActive, toggleProduct } =
    useCompare();
  const { revealBar } = useCompareBarVisibilityActions();
  const toast = useToast();

  const selected = isSelected(compareProduct.id);

  const showBar = () => {
    setActive(true);
    revealBar();
  };

  const handleClick = () => {
    if (selected) {
      if (productCount >= 2) {
        openModal();
      } else {
        showBar();
      }
      return;
    }

    // max number of products for selection reached
    if (!toggleProduct(compareProduct)) {
      toast.add({
        label: `Maximum ${MAX_COMPARE} products for comparison`,
        time: 2500,
      });
      showBar();
      return;
    }

    showBar();
    trackAddedProductToCompare(productCount + 1, {
      productBrand: compareProduct.brand,
      productId: compareProduct.id,
      productKind: compareProduct.kind,
      productName: compareProduct.name,
    });
  };

  return (
    <div className={className} data-testid={testId}>
      <Button
        className={styles['button']}
        variant="tertiary"
        startIcon={selected ? <CheckIcon size="sm" /> : <AddIcon size="sm" />}
        label={productCount > 0 ? `Compare (${productCount})` : 'Compare'}
        onClick={handleClick}
        aria-label={
          selected
            ? `${compareProduct.name} is being compared. ${
                productCount >= 2 ? 'Open comparison.' : 'Show the compare bar.'
              }`
            : `Compare ${compareProduct.name}`
        }
      />
    </div>
  );
};
